import { Grid} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { IOperationsStatus, ModalityType, Operations } from "../../types/FaceVerification";
import {addSession, showMessage, showOperationSummary } from "../../store/reducer";
import { AppContext } from "../../store/context";
import React from "react";
import { RefVideoStream } from "../VideoStream/BiometricFaceStream";
import { useHistory } from "react-router-dom";
import OperationContainer from "../../components/Operations/styled/OperationContainer";
import OperationTitle from "../../components/Operations/styled/OperationTitle";
import VerifySideBar from "./components/VerifySideBar";
import FaceOperation from "../Enroll/Modalities/FaceOperation";
import VoiceOperation from "../Enroll/Modalities/VoiceOperation";
import Overview from "../Overview/Overview";
import { getInitParamsByManagementConfig, getManagementInfo, useOperationStateEffect } from "../../helpers/operationHelper";
import { FVInitParams } from "neurotec-megamatcherid-management-client";
import { startSession } from "../../helpers/webRTC";
import { APIsContext } from "../../store/APIs/context";
import { mmidInitParamsStorage } from "../../config/const";
import { initialFvParameters, useLocalStorage } from "../../helpers/useLocalStorage";
import { NavigationContext } from "../../components/Navigation/store/context";
import { useSnackbar } from "notistack";

const Verify: React.FC = () => {

    const [activeModality, setActiveModality] = useState<ModalityType | null>(null);
    const {state, dispatch} = useContext(AppContext)
    const [options] = useLocalStorage<FVInitParams>(mmidInitParamsStorage, initialFvParameters);
    const apisState = useContext(APIsContext).state
    const history = useHistory();
    const [checkState, setCheckState] = useState<IOperationsStatus>(IOperationsStatus.IDLE);
    const childRef = React.useRef<RefVideoStream>(null)
    const [faceOperationUUID, setFaceOperationUUID] = useState("");
    const [voiceOperationUUID, setVoiceOperationUUID] = useState("");
    const navigationDispatch = useContext(NavigationContext).dispatch
    const [liveAdjudication, setLiveAdjudication] = useState(false)
    const { enqueueSnackbar } = useSnackbar();

    useOperationStateEffect(
        state,
        apisState,
        dispatch,
        checkState,
        (status: IOperationsStatus) => setCheckState(status),
        activeModality,
        (modality: ModalityType | null) => setActiveModality(modality),
        navigationDispatch,
        liveAdjudication,
        enqueueSnackbar,
        faceOperationUUID,
        childRef.current?.cancel,
    )

    useEffect(() => {
        if (state.activeModalities.faceModality) {
            setActiveModality(prev => {
                if (prev == null) {
                    return ModalityType.FACE_MODALITY
                } else {
                    return prev
                }
            })
        }
        if (state.activeModalities.voiceModality) {
            setActiveModality(prev => {
                if (prev == null) {
                    return ModalityType.VOICE_MODALITY
                } else {
                    return prev
                }
            })
        }
    }, [state.activeModalities.faceModality,state.activeModalities.voiceModality, history])

    useEffect(() => {
        if (state.activeModalities.faceModality === false && state.activeModalities.voiceModality === false) {
            history.push("/")
        }
    })

    const restart = () => {
        dispatch(showOperationSummary(""));
        setCheckState(IOperationsStatus.IDLE)
    }

    const verify = async () => {
        getManagementInfo(apisState.ManagementApi).then(res => {
            if (res === null) {
                enqueueSnackbar("Could not connect to the server", {variant: "error"})
                return;
            }
            setLiveAdjudication(res.liveAdjudication)
            let initParams: FVInitParams = getInitParamsByManagementConfig(res, options);
            initParams.subjectId = state.operationState.subjectId;
            initParams.type = Operations.VERIFY;
            startSession(apisState.JanusApi, Operations.VERIFY, initParams).then(res => {
                setCheckState(IOperationsStatus.ACTIVE)
                dispatch(addSession(res.data, initParams));
            }).catch(err => {
                setCheckState(IOperationsStatus.IDLE)
                console.log(err)
                dispatch(showMessage({ message: "Unable to create session: " + err.message, type: "error" }))
            })
        }).catch(e => {
            enqueueSnackbar(e.message, {variant: "error"})
        })
    }
    
    const getName = (): string => {
        switch (activeModality) {
            case ModalityType.FACE_MODALITY:
                return "Face verification"
            case ModalityType.VOICE_MODALITY:
                return "Voice verification"
            case ModalityType.OVERVIEW:
                return "Overview"
            default: 
                return "Verification"
        }
    }

    const getActiveState = () => {
        switch (activeModality) {
            case ModalityType.FACE_MODALITY:
                return (
                    <FaceOperation
                        setCheckState={setCheckState}
                        childRef={childRef}
                        setOperationUUID={setFaceOperationUUID}
                    />
                )
            case ModalityType.VOICE_MODALITY:
                return (
                    <VoiceOperation
                        setCheckState={setCheckState}
                        childRef={childRef}
                        setOperationUUID={setVoiceOperationUUID}
                    />
                )
            case ModalityType.OVERVIEW:
                return  (
                    <Overview
                        faceOperationUUID={faceOperationUUID}
                        voiceOperationUUID={voiceOperationUUID}
                    />
                )
        }
    }

    return (
        <OperationContainer maxWidth={false}>
            <Grid container style={{paddingLeft: "0px", paddingRight: "0px"}}>
                <OperationTitle>
                    {getName()}
                </OperationTitle>
                {getActiveState()}
                <VerifySideBar
                    subjectId={state.operationState.subjectId}
                    activeModality={activeModality}
                    checkState={checkState}
                    start={verify}
                    restart={restart}
                    cancel={childRef.current?.cancel}
                />
            </Grid>
        </OperationContainer>
    );
}

export default Verify
